import { Component } from '@theme/component';

/**
 * How far outside the viewport a clip starts loading. Enough lead time on a
 * slow connection for the first frames to arrive before the clip is scrolled
 * into view, without reaching so far that offscreen sections load anyway.
 */
const LOAD_MARGIN = '200px';

/**
 * A custom element that renders a video background.
 *
 * LOCAL CUSTOMIZATION over upstream Horizon: upstream attaches every source and
 * calls `load()` straight from `connectedCallback`, so every clip in the
 * document downloads at page load whether or not it is on screen. The video
 * carousel duplicates its set twice for the infinite loop, which turned five
 * configured clips into twenty-one simultaneous downloads - roughly 9 MB on a
 * mobile homepage, for a section that sits below every other one.
 *
 * Loading is therefore deferred until the clip approaches the viewport, and
 * clips are paused once they leave it. The markup already stacks the video over
 * its `preview_image`, so an unloaded clip shows that still image rather than a
 * gap - no poster attribute needed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Sources are attached once; re-entering the viewport must not reload them. */
  #loaded = false;

  /** Whether the clip is currently within the load margin of the viewport. */
  #visible = false;

  /** Bound so the same reference can be removed on disconnect. */
  #onCanPlay = () => this.#tryPlay();

  connectedCallback() {
    super.connectedCallback();

    // The stylesheet hides the <video> outright under reduced motion, so
    // downloading it would spend the visitor's data on something never shown.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    if (!('IntersectionObserver' in window)) {
      this.#loadSources();
      return;
    }

    // Playback is attempted again on `canplay` because sources are attached
    // lazily: the first attempt fires while the clip still holds no frames and
    // is rejected, and without this the clip would sit loaded but paused.
    this.refs.videoElement.addEventListener('canplay', this.#onCanPlay);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#visible = entry.isIntersecting;

          if (entry.isIntersecting) {
            this.#loadSources();
            this.#tryPlay();
          } else if (this.#loaded) {
            this.refs.videoElement.pause?.();
          }
        }
      },
      { rootMargin: LOAD_MARGIN }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.videoElement?.removeEventListener('canplay', this.#onCanPlay);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Autoplay is declared on the element, but a clip whose sources arrive after
   * the initial parse needs an explicit nudge. A rejected promise only means
   * the browser declined playback, which is not an error worth surfacing.
   */
  #tryPlay() {
    if (!this.#visible) return;

    this.refs.videoElement.play?.().catch(() => {});
  }

  #loadSources() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
